// OWNED BY the signatures implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/signatures/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// חתימות tab (mock s25): the contact's e-sign documents grouped by lifecycle —
// ממתינים לחתימה (live per-signer progress + reminders) / טיוטות (send flow) /
// הושלמו (branded viewer + downloads, auto-filed into the documents tab) — with
// a "new signature request" action on top. Data layer + legacy-logic ports
// live under ./signatures/ (signaturesData.ts).

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Divider, Skeleton, Stack, Typography } from "@material-ui/core";
import { Add as AddIcon, HistoryEdu as HistoryEduIcon } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import EmptyContent from "components/EmptyContent";
import Label from "components/Label";
import type { LabelColor } from "components/Label";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { buildNewSignatureParams } from "components/_dashboard/signatures/GlobalNewSignatureDialog";
import { quickPickRoleForContact } from "components/_dashboard/signatures/NewSignatureForm";
import { TabCard } from "../TabCard";
import { getCached, subscribeCached, useSessionCacheValue } from "../data/sessionCache";
import {
  contactPrefillListingIdForRole,
  countPendingSignatures,
  groupContactSignatures,
  loadContactSignatures,
  preloadContactSignatures,
  SIGNATURES_MAX_AGE_MS,
  signaturesCacheKey,
  subscribeContactSignaturesLive
} from "./signatures/signaturesData";
import { SignatureDocRow } from "./signatures/SignatureDocRow";
import { signingSetOf } from "utils/signatureQuestionnaireStep";
import type { SignatureDocument } from "../../../../../@types/signatures";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

export default function SignaturesTab({ contact }: ContactTabComponentProps): JSX.Element {
  const { user } = useAuth();
  const { userSettings } = useFirestore();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasSignatureFeatureFlag = userSettings?.featureFlagsV2?.includes("signature") ?? false;
  const ownerEmail: string | undefined = user?.email ?? undefined;

  const cacheKey = signaturesCacheKey(contact.id);
  const { data: docs, loading, error } = useSessionCacheValue<SignatureDocument[]>(
    cacheKey,
    () => loadContactSignatures(contact),
    { maxAgeMs: SIGNATURES_MAX_AGE_MS }
  );

  // Live layer: one light Firestore listener for this contact's documents —
  // signer status flips / WhatsApp delivery / completion land in the shared
  // cache (tab body + badge) in realtime. Subscribed on first tab mount and
  // kept alive while the customer page is open (the tab stays mounted after
  // the first visit — cleanup runs on page close/unmount).
  useEffect(() => {
    if (!hasSignatureFeatureFlag || !ownerEmail || !contact?.id) return undefined;
    return subscribeContactSignaturesLive(contact, ownerEmail);
    // The contact OBJECT identity changes on live updates; the subscription only
    // depends on the stable id/phone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSignatureFeatureFlag, ownerEmail, contact.id]);

  const isColdStart = loading && docs === undefined;

  useEffect(() => {
    if (hasSignatureFeatureFlag && error && docs === undefined) {
      enqueueSnackbar("טעינת מסמכי החתימה נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Opens the globally-mounted new-signature dialog via URL params (MERGE
  // pattern — ?contact= / ?tab= / the dialog params share one URL). The
  // contact's customer type pre-selects the signing scenario, so the matching
  // default template is auto-picked (same as the header quick action). For a
  // seller-side scenario (מוכר/משכיר) the contact's own listing — first of
  // sellingListingIds/rentingOutListingIds per the role — is prefilled into
  // the send flow's listing picker; buyer-side contacts get no listing param.
  const openNewSignatureDialog = (): void => {
    const next = new URLSearchParams(searchParams);
    const role = quickPickRoleForContact(contact) ?? undefined;
    const prefillListingId = contactPrefillListingIdForRole(contact, role);
    const params = buildNewSignatureParams({
      contactId: contact.id,
      role,
      listingIds: prefillListingId ? [prefillListingId] : undefined
    });
    Object.entries(params).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  if (!hasSignatureFeatureFlag) {
    return (
      <TabCard sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
        <EmptyContent
          title="חתימות דיגיטליות אינן פעילות"
          description="החתמת לקוחות על מסמכים דיגיטליים אינה מופעלת בחשבון שלכם. פנו לצוות Webot כדי להפעיל את הפיצ'ר."
        />
      </TabCard>
    );
  }

  const allDocs = docs ?? [];
  const groups = groupContactSignatures(allDocs);
  const isEmpty = !isColdStart && allDocs.length === 0;
  // The central empty-state CTA renders exactly when the list loaded empty
  // (error-empty shows the error text instead). While it's on screen, the
  // header action button is hidden — one "בקשת חתימה חדשה" at a time.
  const showEmptyState = isEmpty && !error;

  return (
    <TabCard sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
      {/* header */}
      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
        <Typography variant="h6">חתימות דיגיטליות</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          מעקב חי · תזכורות בוואטסאפ
        </Typography>
      </Stack>

      {/* action row: new request. Hidden while the empty state is on screen —
          its central CTA is the single entry point then (mock s25 deviation,
          deliberate: the quick-template-chips row was removed — the role
          derived from the contact already auto-picks the default template). */}
      {!showEmptyState && (
        <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={openNewSignatureDialog}
          >
            בקשת חתימה חדשה
          </Button>
        </Stack>
      )}

      {isColdStart ? (
        <SignaturesListSkeleton />
      ) : error && allDocs.length === 0 ? (
        <Typography variant="body2" color="error" sx={{ py: 4, textAlign: "center" }}>
          טעינת מסמכי החתימה נכשלה — נסו לרענן את העמוד
        </Typography>
      ) : showEmptyState ? (
        <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", py: 6, px: 2 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "primary.lighter",
              color: "primary.main",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <HistoryEduIcon sx={{ fontSize: 36 }} />
          </Box>
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h6">אין עדיין מסמכים לחתימה</Typography>
            <Typography variant="body2" color="text.secondary">
              שלחו ללקוח מסמך לחתימה דיגיטלית — הסכם תיווך, הצעת רכש או כל תבנית אחרת.
            </Typography>
          </Stack>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={openNewSignatureDialog}>
            בקשת חתימה חדשה
          </Button>
        </Stack>
      ) : (
        <Stack sx={{ mt: 2 }}>
          <SignatureGroup title="ממתינים לחתימה" color="warning" docs={groups.pending} allDocs={allDocs} contact={contact} />
          <SignatureGroup title="טיוטות" color="default" docs={groups.drafts} allDocs={allDocs} contact={contact} />
          <SignatureGroup title="הושלמו" color="success" docs={groups.completed} allDocs={allDocs} contact={contact} />
          <SignatureGroup title="נדחו / פגי תוקף" color="error" docs={groups.closed} allDocs={allDocs} contact={contact} />
        </Stack>
      )}
    </TabCard>
  );
}

function SignatureGroup({
  title,
  color,
  docs,
  allDocs,
  contact
}: {
  title: string;
  color: LabelColor;
  docs: SignatureDocument[];
  /** Every document of this client: a row's SET may sit in another group (one contract signed, one not). */
  allDocs: SignatureDocument[];
  contact: RealEstateContact;
}): JSX.Element | null {
  if (docs.length === 0) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ mt: 1.5, mb: 0.5, pb: 0.75, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        <Label color={color} variant="ghost">
          {docs.length}
        </Label>
      </Stack>
      <Stack divider={<Divider sx={{ borderStyle: "dashed" }} />}>
        {docs.map((doc) => (
          <SignatureDocRow key={doc.id} contact={contact} doc={doc} setDocuments={signingSetOf(doc, allDocs)} />
        ))}
      </Stack>
    </Box>
  );
}

function SignaturesListSkeleton(): JSX.Element {
  return (
    <Stack gap={3} sx={{ mt: 3 }}>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" gap={1.5} alignItems="flex-start">
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1.5 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="35%" />
            <Skeleton variant="text" width="70%" />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: count of documents still awaiting signature
// (sent/partial), in warning color. Reads ONLY from the session cache (warmed
// by preload, kept live by the tab's Firestore listener) — never fetches.
export function useSignaturesTabBadge(contact: RealEstateContact): TabBadge {
  const contactId = contact?.id ?? "";
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!contactId) return undefined;
    return subscribeCached(signaturesCacheKey(contactId), () => forceRender((n) => n + 1));
  }, [contactId]);

  const cachedDocs = contactId
    ? getCached<SignatureDocument[]>(signaturesCacheKey(contactId))
    : undefined;
  const pendingCount = countPendingSignatures(cachedDocs);
  if (!pendingCount) return null;
  return { count: pendingCount, color: "warning" };
}

// Cache warm-up fired on contacts-table row hover and page mount. Idempotent
// and cheap: deduped through fetchCached (stale-while-revalidate), and it
// stops re-firing for the session once the server says the user lacks the
// signature feature flag (403).
export function preloadSignaturesTab(contact: RealEstateContact): void {
  preloadContactSignatures(contact);
}
